import { Transition } from '@headlessui/react'
import clsx from 'clsx'
import Button from 'components/tailwind/Button'
import { Heading } from 'components/ui/Heading'
import { DiscoverContext } from 'features/discover/context/DiscoverContext'
import { useContext, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'

type CtaProps = {
  title: string
  onClick: () => void
}

type VideoMessageProps = {
  className?: string
  title?: string
  description: string
  videoSrc: string
  ctaPrimary: CtaProps
  ctaSecondary?: CtaProps
  isPulsing?: boolean
}

export default function VideoMessage({
  className,
  title,
  description,
  videoSrc,
  ctaPrimary,
  ctaSecondary,
  isPulsing
}: VideoMessageProps) {
  const discoverContext = useContext(DiscoverContext)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showVideo, setShowVideo] = useState(true)
  const [ctaVisible, setCtaVisible] = useState(true)
  const [localIsMuted, setLocalIsMuted] = useState(true)
  const isMuted = discoverContext?.assistantVideoMuted ?? localIsMuted
  const setIsMuted = discoverContext?.setAssistantVideoMuted ?? setLocalIsMuted

  const handleCtaClick = (onClick: () => void) => {
    setCtaVisible(false)
    stopVideo()
    setTimeout(onClick, 300) // Animation duration
  }

  const stopVideo = () => {
    const v = videoRef.current
    if (v && !v.paused) {
      v.pause()
    }
  }

  const handleVolumeChange = () => {
    const v = videoRef.current
    if (!v) return

    const nextMuted = v.muted
    setIsMuted((prev) => (prev === nextMuted ? prev : nextMuted))
  }

  useEffect(() => {
    if (!showVideo) return
    const v = videoRef.current
    if (!v) return
    v.muted = isMuted
    if (v.paused) {
      const p = v.play()
      if (p && typeof p.catch === 'function') {
        p.catch(() => {})
      }
    }
  }, [showVideo])

  return (
    <div
      className={clsx(
        'w-full rounded-lg border border-lightGrey bg-lightPaleGrey p-4',
        className
      )}
    >
      {/* <div className='mb-2 flex justify-end'>
        <div
          className={clsx(
            'inline-flex items-center gap-2 rounded border border-lightGrey bg-white px-2 py-1 transition-all duration-300',
            toolbarOpen
              ? 'scale-100 opacity-100'
              : 'pointer-events-none scale-95 opacity-0'
          )}
          aria-label='Ansicht wechseln'
        >
          <button
            aria-label='Text anzeigen/ausblenden'
            className={clsx(
              'rounded p-1 transition',
              showText ? 'bg-blue-100 text-blue-700' : 'hover:bg-lightGrey'
            )}
            onClick={toggleText}
            title='Text'
            tabIndex={toolbarOpen ? 0 : -1}
          >
            <span role='img' aria-label='Text'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='size-4'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z'
                />
              </svg>
            </span>
          </button>
          <button
            aria-label='Video anzeigen/ausblenden'
            className={clsx(
              'rounded p-1 transition',
              showVideo ? 'bg-blue-100 text-blue-700' : 'hover:bg-lightGrey'
            )}
            onClick={toggleVideo}
            title='Video'
            tabIndex={toolbarOpen ? 0 : -1}
          >
            <span role='img' aria-label='Video'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='size-4'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z'
                />
              </svg>
            </span>
          </button>
        </div>
      </div> */}
      {title && <Heading level={5}>{title}</Heading>}
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p className='mb-5 block text-sm/normal text-darkBlack'>
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className='mb-5 list-disc pl-5 text-sm text-darkBlack'>
              {children}
            </ul>
          ),
          li: ({ children }) => <li className='mb-1'>{children}</li>
        }}
        className={clsx(
          'text-sm font-normal text-darkBlack',
          title ? 'mt-1' : 'mt-0'
        )}
      >
        {description}
      </ReactMarkdown>

      <div className={clsx('relative mt-3')}>
        <video
          ref={videoRef}
          className='w-full rounded-md'
          src={videoSrc}
          autoPlay
          controls
          muted={isMuted}
          playsInline
          preload='metadata'
          aria-label={title || 'Video'}
          onVolumeChange={handleVolumeChange}
        >
          Dein Browser unterstützt das Video-Tag nicht.
        </video>
        {/* <button
            type='button'
            aria-pressed={!isMuted}
            aria-label={isMuted ? 'Ton einschalten' : 'Ton ausschalten'}
            onClick={toggleMute}
            className='absolute left-3 top-3 rounded-full bg-black/70 p-2 text-white transition hover:bg-black/80'
          >
            {isMuted ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='size-4'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M9.594 5.25 6 8.25H3.75A.75.75 0 0 0 3 9v6a.75.75 0 0 0 .75.75H6l3.594 3a.75.75 0 0 0 1.218-.586V5.836a.75.75 0 0 0-1.218-.586Zm4.656 1.031a9 9 0 0 1 0 11.438m2.25-9.188a5.25 5.25 0 0 1 0 6.938'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='size-4'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M11.25 5.836a.75.75 0 0 0-1.218-.586L6 8.25H3.75A.75.75 0 0 0 3 9v6a.75.75 0 0 0 .75.75H6l4.032 3.25a.75.75 0 0 0 1.218-.585V5.836Zm8.721 4.16a5.25 5.25 0 0 1 0 4.008m2.041-5.878a8.25 8.25 0 0 1 0 7.748M15 8.25v7.5'
                />
              </svg>
            )}
          </button> */}
        {/* {videoDuration !== null && (
            <span className='absolute bottom-2 right-2 rounded-full bg-black/70 px-2 py-0.5 text-xs font-medium text-white'>
              {videoDuration}s
            </span>
          )} */}
      </div>

      <Transition
        show={ctaVisible}
        enter='transition-opacity duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-300'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div className='mt-4 flex flex-col gap-y-2'>
          {ctaPrimary && (
            <Button.PrimaryBase
              text={ctaPrimary.title}
              isPulsing={isPulsing}
              onClick={() => handleCtaClick(ctaPrimary.onClick)}
            />
          )}
          {ctaSecondary && (
            <Button.SecondaryBase
              text={ctaSecondary.title}
              onClick={() => handleCtaClick(ctaSecondary.onClick)}
            />
          )}
        </div>
      </Transition>
    </div>
  )
}
